import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, SetStateAction, useEffect} from 'react';

import {assert} from 'Asserts';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import FiltersContainer from 'search/filters/FiltersContainer';
import {Filter, FilterKey, Operator, StandardRangeOperatorValue} from 'search/SearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {BenchlingAppActivitySessionsQuery} from './__generated/BenchlingAppActivitySessionsQuery';

type BenchlingAppSessionNode = ExtractByTypename<
  BenchlingAppActivitySessionsQuery['benchlingApp'],
  'BenchlingAppNode'
>['sessionListing']['sessions'][number];

export interface SessionActivityFiltersProps {
  modifiedAtFilter: {
    fromDatetime: string | null;
    toDatetime: string | null;
  };
  setModifiedAtFilter: Dispatch<
    SetStateAction<{
      fromDatetime: string | null;
      toDatetime: string | null;
    }>
  >;
  sessionNameFilter: string | null;
  setSessionNameFilter: Dispatch<SetStateAction<string | null>>;
  sessionStatusFilter: Array<string>;
  setSessionStatusFilter: Dispatch<SetStateAction<Array<string>>>;
  allSessions: Array<BenchlingAppSessionNode>;
  setFilteredSessions: Dispatch<SetStateAction<Array<BenchlingAppSessionNode>>>;
  setOffset: (offset: number) => void;
}

export default function BenchlingAppSessionActivityFilters(props: SessionActivityFiltersProps): JSX.Element {
  const allSessions = props.allSessions;
  useEffect(() => {
    filterSessions(props, allSessions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={css(styles.filterStyle)}>
      <FiltersContainer
        primaryFilterOptions={[
          {filterKey: FilterKey.MODIFIED_AT},
          {filterKey: FilterKey.SESSION_NAME},
          {filterKey: FilterKey.SESSION_STATUS},
        ]}
        filters={[
          {
            key: FilterKey.MODIFIED_AT,
            value: {from: props.modifiedAtFilter.fromDatetime, to: props.modifiedAtFilter.toDatetime},
            operator: Operator.BETWEEN,
          },
          {key: FilterKey.SESSION_NAME, value: props.sessionNameFilter, operator: Operator.CONTAINS},
          {
            key: FilterKey.SESSION_STATUS,
            value: props.sessionStatusFilter,
            operator: Operator.IS_ONE_OF,
          },
        ]}
        onChangeFilters={(filters) => {
          onChangeFilters(props, allSessions, filters);
        }}
        selectedSchema={null}
        clearButton={
          <Button
            buttonStyle={ButtonStyle.CIRCLE_ICON}
            size={ButtonSize.SMALL}
            onClick={() => {
              clearFilters(props, allSessions);
            }}
            icon="cross-thick"
          />
        }
      />
    </div>
  );
}

export function clearFilters(
  props: SessionActivityFiltersProps,
  allSessions: Array<BenchlingAppSessionNode>
): void {
  props.setOffset(0);
  props.setModifiedAtFilter({
    fromDatetime: null,
    toDatetime: null,
  });
  props.setSessionNameFilter(null);
  props.setSessionStatusFilter([]);
  props.setFilteredSessions(allSessions);
}

export function filterSessions(
  props: SessionActivityFiltersProps,
  allSessions: Array<BenchlingAppSessionNode>
): void {
  props.setFilteredSessions(
    allSessions.filter(
      (session) =>
        (props.modifiedAtFilter.fromDatetime == null ||
          props.modifiedAtFilter.fromDatetime === '' ||
          session.modifiedAt > props.modifiedAtFilter.fromDatetime) &&
        (props.modifiedAtFilter.toDatetime == null ||
          props.modifiedAtFilter.toDatetime === '' ||
          session.modifiedAt < props.modifiedAtFilter.toDatetime) &&
        (props.sessionNameFilter == null || session.name.includes(props.sessionNameFilter)) &&
        (props.sessionStatusFilter.length === 0 || props.sessionStatusFilter.includes(session.status))
    )
  );
}

export function onChangeFilters(
  props: SessionActivityFiltersProps,
  allSessions: Array<BenchlingAppSessionNode>,
  filters: Array<Filter>
): void {
  props.setOffset(0);
  let newModifiedAtFilter: {
    fromDatetime: string | null;
    toDatetime: string | null;
  } = props.modifiedAtFilter;
  let newSessionNameFilter: string | null = props.sessionNameFilter;
  let newSessionStatusFilter: Array<string> = props.sessionStatusFilter;
  for (const filter of filters) {
    switch (filter.key) {
      case FilterKey.MODIFIED_AT:
        // eslint-disable-next-line no-case-declarations
        const value = filter.value as StandardRangeOperatorValue;
        // eslint-disable-next-line no-case-declarations
        const convertedValue = {fromDatetime: value.from as string, toDatetime: value.to as string};
        props.setModifiedAtFilter(convertedValue);
        newModifiedAtFilter = convertedValue;
        break;
      case FilterKey.SESSION_NAME:
        props.setSessionNameFilter(filter.value);
        newSessionNameFilter = filter.value;
        break;
      case FilterKey.SESSION_STATUS:
        props.setSessionStatusFilter([...filter.value]);
        newSessionStatusFilter = [...filter.value];
        break;
      default:
        assert(false, `Unexpected filter key in BenchlingAppSessionActivityFilters: ${filter.key}`);
    }
  }
  filterSessions(
    {
      ...props,
      modifiedAtFilter: newModifiedAtFilter,
      sessionNameFilter: newSessionNameFilter,
      sessionStatusFilter: newSessionStatusFilter,
    },
    allSessions
  );
}

export const styles = StyleSheet.create({
  filterStyle: {
    margin: '10px',
  },
});
